import { useId, useMemo, useRef, useState } from "react";
import { Popover } from "react-aria-components";

import { Clock, EmptystateSearchNoResultsColored, Trash } from "components/icons/generated";
import Box from "ds/components/Box";
import DropdownWrapper from "ds/components/Dropdown/DropdownWrapper";
import DropdownHeaderSearch from "ds/components/Dropdown/Header/Search";
import TextEllipsis from "ds/components/TextEllipsis";
import Typography from "ds/components/Typography";
import useEscapeKeypress from "hooks/useEscapeKeyPress";
import useOutsideClick from "hooks/useOutsideClick";
import ButtonIcon from "ds/components/ButtonIcon";
import DropdownSection from "ds/components/Dropdown/Section";
import DropdownSectionItem from "ds/components/Dropdown/SectionItem";
import DropdownListWrapper from "ds/components/Dropdown/List/New/Wrapper";
import EmptyState from "ds/components/EmptyState";

import styles from "./styles.module.css";
import { AiChatSession } from "../types";
import { groupAiChatHistoryChatsByUpdatedAt } from "./utils";

type AiChatHistoryDropdownProps = {
  deleteChat: (chatId: string) => void;
  chats: AiChatSession[];
  currentChatId: string;
  loading: boolean;
  onSelectChat: (chatId: string) => void;
};

const AiChatHistoryDropdown = ({
  deleteChat,
  chats,
  currentChatId,
  loading,
  onSelectChat,
}: AiChatHistoryDropdownProps) => {
  const wrapperRef = useRef(null);
  const dropdownId = useId();
  const triggerRef = useRef(null);
  const listboxId = useId();
  const [isVisible, setIsVisible] = useState(false);
  const [searchInput, setSearchInput] = useState("");

  const filteredChats = useMemo(() => {
    return chats.filter((chat) => chat.name.toLowerCase().includes(searchInput.toLowerCase()));
  }, [chats, searchInput]);

  const sections = useMemo(() => {
    return groupAiChatHistoryChatsByUpdatedAt(filteredChats, new Date());
  }, [filteredChats]);

  const handleToggleDropdown = () => {
    setIsVisible(!isVisible);
  };

  const closeDropdown = () => {
    setIsVisible(false);
  };

  const noSearchResults = filteredChats.length === 0 && chats.length > 0;

  useOutsideClick(wrapperRef, closeDropdown);
  useEscapeKeypress(isVisible, closeDropdown);

  return (
    <DropdownWrapper ref={wrapperRef}>
      <ButtonIcon
        icon={Clock}
        variant="ghost"
        size="small"
        onPress={handleToggleDropdown}
        active={isVisible}
        ref={triggerRef}
        aria-controls={dropdownId}
        aria-expanded={isVisible}
      >
        History
      </ButtonIcon>

      <Popover
        triggerRef={triggerRef}
        isOpen={isVisible}
        onOpenChange={handleToggleDropdown}
        placement="bottom left"
      >
        <DropdownListWrapper id={dropdownId} className={styles.dropdownList}>
          <DropdownHeaderSearch
            listboxId={listboxId}
            ariaExpanded={true}
            ariaLabel="Ai chat history"
            searchPlaceholder="Search chats..."
            searchValue={searchInput}
            setSearchValue={setSearchInput}
            searchLoading={loading}
          />

          {noSearchResults && (
            <EmptyState
              title="No search results"
              icon={EmptystateSearchNoResultsColored}
              fullHeight
            />
          )}
          <Box direction="column" scrollable>
            {sections.map((section) => (
              <DropdownSection
                topDivider={false}
                title={section.label}
                key={section.label}
                className={styles.dropdownSection}
              >
                {section.chats.map((chat) => (
                  <Box relative key={chat.id}>
                    <DropdownSectionItem
                      onClick={() => onSelectChat(chat.id)}
                      active={chat.id === currentChatId}
                      className={styles.dropdownSectionItem}
                    >
                      <Box direction="row" align="center" gap="large" fullWidth justify="between">
                        <TextEllipsis tooltip={chat.name} tooltipWidthMode="maxWidthXl">
                          {(props) => (
                            <Typography {...props} variant="p-body3" color="primary" tag="span">
                              {chat.name}
                            </Typography>
                          )}
                        </TextEllipsis>
                      </Box>
                    </DropdownSectionItem>
                    <ButtonIcon
                      className={styles.deleteButton}
                      icon={Trash}
                      variant="ghostDanger"
                      size="small"
                      onPress={() => deleteChat(chat.id)}
                    >
                      Delete
                    </ButtonIcon>
                  </Box>
                ))}
              </DropdownSection>
            ))}
          </Box>
        </DropdownListWrapper>
      </Popover>
    </DropdownWrapper>
  );
};

export default AiChatHistoryDropdown;
